import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { Upload, X, Loader2, Eye, EyeOff, ArrowLeft, Save, FileText, Image, Layers, CheckCircle, Send, AlertTriangle } from 'lucide-react';

const STATUS_COLORS = {
  draft:    { color: '#6b7280', bg: '#f3f4f6', label: 'Draft' },
  pending:  { color: '#f59e0b', bg: '#fef3c7', label: 'Pending Review' },
  approved: { color: '#16a34a', bg: '#dcfce7', label: 'Approved' },
  rejected: { color: '#dc2626', bg: '#fee2e2', label: 'Rejected' },
};

const SUBJECTS = ['Mathematics', 'Physics', 'Chemistry'];
const DIFFICULTIES = ['Easy', 'Medium', 'Hard'];
const CODE_PREFIX = { Mathematics: 'MATH', Physics: 'PHY', Chemistry: 'CHE' };
const subjectColors = {
  Mathematics: 'text-blue-700 bg-blue-50 border-blue-200',
  Physics: 'text-green-700 bg-green-50 border-green-200',
  Chemistry: 'text-amber-700 bg-amber-50 border-amber-200',
};
const diffColors = { Easy: 'text-green-700 bg-green-100', Medium: 'text-amber-700 bg-amber-100', Hard: 'text-red-700 bg-red-100' };

const INIT = {
  questionCode: '', questionType: 'text', subject: 'Mathematics',
  difficulty: 'Medium', marks: 1,
  questionText: '', formula: '', questionImage: '',
  optA: '', optB: '', optC: '', optD: '',
  optImgA: '', optImgB: '', optImgC: '', optImgD: '',
  correctAnswer: 'A', explanation: '',
};

function ImgUploadBtn({ value, onChange, label }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const { data } = await api.post('/questions/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (data.success) {
        onChange(data.imageUrl);
        toast.success('Image uploaded');
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  return (
    <div>
      <button type="button" onClick={() => ref.current.click()}
        className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-dashed border-gray-300 rounded-lg hover:border-gray-500 hover:bg-gray-50 transition-colors text-gray-500">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} {label}
      </button>
      <input type="file" accept="image/*" ref={ref} className="hidden" onChange={handle} />
      {value && (
        <div className="relative inline-block mt-2">
          <img src={value} alt="preview" className="h-24 rounded-lg border border-gray-200 object-contain bg-gray-50" />
          <button type="button" onClick={() => onChange('')}
            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center shadow-sm hover:bg-red-600">
            <X size={10} />
          </button>
        </div>
      )}
    </div>
  );
}

export default function AddQuestionPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { isSuperAdmin } = useAuth();
  const [form, setForm] = useState({ ...INIT });
  const [questionStatus, setQuestionStatus] = useState('draft');
  const [saving, setSaving] = useState(false);
  const [submitAfterSave, setSubmitAfterSave] = useState(false);
  const [preview, setPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const superadmin = isSuperAdmin();

  const sf = (patch) => setForm(f => ({ ...f, ...patch }));

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      api.get(`/questions/${id}`).then(({ data }) => {
        const q = data.data;
        setQuestionStatus(q.status || 'draft');
        setForm({
          questionCode: q.questionCode || '',
          questionType: q.questionType || 'text',
          subject: q.subject || 'Mathematics',
          difficulty: q.difficulty || 'Medium',
          marks: q.marks || 1,
          questionText: q.questionText || '',
          formula: q.formula || '',
          questionImage: q.questionImage || '',
          optA: q.options?.A || '', optB: q.options?.B || '',
          optC: q.options?.C || '', optD: q.options?.D || '',
          optImgA: q.optionImages?.A || '', optImgB: q.optionImages?.B || '',
          optImgC: q.optionImages?.C || '', optImgD: q.optionImages?.D || '',
          correctAnswer: q.correctAnswer || 'A',
          explanation: q.explanation || '',
        });
      }).catch(() => toast.error('Failed to load question'))
        .finally(() => setLoading(false));
    }
  }, [id]);

  const validate = () => {
    if (!form.questionCode.trim()) return 'Question Code is required';
    if (!/^[A-Z]{2,6}\d{1,6}$/.test(form.questionCode.trim())) return 'Code format: MATH001, PHY023, CHE045…';
    if (!form.subject) return 'Subject is required';
    if (!form.correctAnswer) return 'Correct answer is required';
    if (form.questionType !== 'image' && !form.questionText.trim()) return 'Question text is required';
    if ((form.questionType === 'image' || form.questionType === 'hybrid') && !form.questionImage) return 'Please upload a question image';
    if (!form.optA || !form.optB || !form.optC || !form.optD) return 'All four option texts are required';
    return null;
  };

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const err = validate(); if (err) return toast.error(err);
    setSaving(true);
    try {
      const payload = {
        questionCode: form.questionCode.toUpperCase(),
        questionType: form.questionType,
        questionText: form.questionText,
        formula: form.formula,
        questionImage: form.questionImage,
        options: { A: form.optA, B: form.optB, C: form.optC, D: form.optD },
        optionImages: { A: form.optImgA, B: form.optImgB, C: form.optImgC, D: form.optImgD },
        correctAnswer: form.correctAnswer,
        explanation: form.explanation,
        subject: form.subject,
        difficulty: form.difficulty,
        marks: Number(form.marks) || 1,
        status: 'approved',   // always approved — no draft/approval flow
      };
      if (isEdit) {
        await api.put(`/questions/${id}`, payload);
        toast.success('Question updated!');
      } else {
        await api.post('/questions', payload);
        toast.success('Question saved!');
      }
      navigate('/admin/questions');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const autoCode = (subject) => {
    const p = CODE_PREFIX[subject];
    const n = String(Math.floor(Math.random() * 900 + 100));
    return `${p}${n}`;
  };

  if (loading) return <div className="flex justify-center items-center h-64"><div className="loader"></div></div>;

  return (
    <div className="max-w-4xl mx-auto space-y-0 animate-fade-in">
      {/* Page header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/admin/questions')} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{isEdit ? 'Edit Question' : 'Add New Question'}</h1>
          <p className="text-gray-500 text-sm mt-0.5">Fill in all sections carefully. All fields marked * are required.</p>
        </div>
        {isEdit && (() => {
          const sc = STATUS_COLORS[questionStatus] || STATUS_COLORS.draft;
          return (
            <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', color: sc.color, background: sc.bg, marginLeft: '4px' }}>
              {sc.label}
            </span>
          );
        })()}
        <button type="button" onClick={() => setPreview(p => !p)}
          className="ml-auto flex items-center gap-2 btn-secondary text-sm">
          {preview ? <EyeOff size={15} /> : <Eye size={15} />} {preview ? 'Edit' : 'Preview'}
        </button>
      </div>

      {/* Approved question warning */}
      {isEdit && questionStatus === 'approved' && !superadmin && (
        <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <AlertTriangle size={18} color="#f59e0b" />
          <p style={{ margin: 0, fontSize: '13px', color: '#92400e', fontWeight: '600' }}>
            This question is approved. Editing it will reset it to Draft status and require re-approval.
          </p>
        </div>
      )}

      {preview ? (
        /* ── PREVIEW ── */
        <div className="card space-y-4">
          <h3 className="font-bold text-gray-800 border-b pb-3">Preview</h3>
          <div className="flex gap-2 flex-wrap">
            <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${subjectColors[form.subject]}`}>{form.subject}</span>
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${diffColors[form.difficulty]}`}>{form.difficulty}</span>
            <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded text-gray-700">{form.questionCode}</span>
            <span className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">{form.marks} mark{form.marks !== 1 ? 's' : ''}</span>
          </div>
          {form.questionImage && <img src={form.questionImage} alt="Question" className="max-w-md rounded-lg border border-gray-200" />}
          {form.questionText && <p className="text-gray-900 text-base font-medium">{form.questionText}</p>}
          {form.formula && <div className="bg-blue-50 border border-blue-100 text-blue-700 font-mono text-sm px-3 py-2 rounded-lg">{form.formula}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            {['A','B','C','D'].map(opt => (
              <div key={opt} className={`border rounded-xl p-3 flex items-start gap-3 ${form.correctAnswer === opt ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-white'}`}>
                <span className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-sm ${form.correctAnswer === opt ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600'}`}>{opt}</span>
                <div>
                  {form[`optImg${opt}`] && <img src={form[`optImg${opt}`]} alt={`opt-${opt}`} className="h-16 mb-1 rounded object-contain" />}
                  <p className="text-sm text-gray-800">{form[`opt${opt}`]}</p>
                  {form.correctAnswer === opt && <p className="text-xs text-green-600 font-semibold mt-0.5">✓ Correct Answer</p>}
                </div>
              </div>
            ))}
          </div>
          {form.explanation && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Explanation</p>
              <p className="text-sm text-gray-700">{form.explanation}</p>
            </div>
          )}
          <button type="button" onClick={() => setPreview(false)} className="btn-secondary text-sm">← Back to Edit</button>
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-5">

          {/* ── SECTION 1: Basic Details ── */}
          <div className="card space-y-4">
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider border-b pb-2">Section 1 — Basic Details</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <select className="input-field" value={form.subject}
                  onChange={e => sf({ subject: e.target.value, questionCode: autoCode(e.target.value) })}>
                  {SUBJECTS.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                <select className="input-field" value={form.difficulty} onChange={e => sf({ difficulty: e.target.value })}>
                  {DIFFICULTIES.map(d => <option key={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Marks</label>
                <input type="number" min="0.25" step="0.25" className="input-field" value={form.marks} onChange={e => sf({ marks: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Question Code *</label>
                <input type="text" className="input-field font-mono uppercase" placeholder={`${CODE_PREFIX[form.subject]}001`}
                  value={form.questionCode} onChange={e => sf({ questionCode: e.target.value.toUpperCase() })} />
                <p className="text-xs text-gray-400 mt-1">e.g. MATH001, PHY023</p>
              </div>
            </div>
          </div>

          {/* ── SECTION 2: Question Type ── */}
          <div className="card space-y-4">
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider border-b pb-2">Section 2 — Question Type</h2>
            <div className="grid grid-cols-3 gap-3">
              {[
                { val: 'text', label: 'Text Only', icon: <FileText size={18} />, desc: 'Plain text with optional LaTeX formula' },
                { val: 'image', label: 'Image Only', icon: <Image size={18} />, desc: 'Upload an image as the question' },
                { val: 'hybrid', label: 'Text + Image', icon: <Layers size={18} />, desc: 'Both text and an image together' },
              ].map(({ val, label, icon, desc }) => (
                <button key={val} type="button" onClick={() => sf({ questionType: val })}
                  className={`flex flex-col items-center gap-2 py-4 px-3 rounded-xl border-2 text-sm font-medium transition-all ${form.questionType === val ? 'border-black bg-black text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400 bg-white'}`}>
                  {icon}
                  <span className="font-semibold">{label}</span>
                  <span className={`text-xs text-center leading-snug ${form.questionType === val ? 'text-gray-300' : 'text-gray-400'}`}>{desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ── SECTION 3: Question Input ── */}
          <div className="card space-y-4">
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider border-b pb-2">Section 3 — Question Content</h2>

            {(form.questionType === 'text' || form.questionType === 'hybrid') && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Question Text {form.questionType === 'text' ? '*' : ''}
                  </label>
                  <textarea className="input-field" rows={4} placeholder="Type the full question here..."
                    value={form.questionText} onChange={e => sf({ questionText: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    LaTeX Formula <span className="text-gray-400 text-xs">(optional — e.g. \frac{'{1}'}{'{2}'} )</span>
                  </label>
                  <input type="text" className="input-field font-mono text-sm" placeholder="\frac{1}{2}mv^2"
                    value={form.formula} onChange={e => sf({ formula: e.target.value })} />
                  {form.formula && (
                    <div className="mt-2 bg-blue-50 border border-blue-100 text-blue-700 font-mono text-sm px-3 py-2 rounded-lg">{form.formula}</div>
                  )}
                </div>
              </div>
            )}

            {(form.questionType === 'image' || form.questionType === 'hybrid') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Question Image {form.questionType === 'image' ? '*' : '(optional for hybrid)'}
                </label>
                <ImgUploadBtn value={form.questionImage} onChange={v => sf({ questionImage: v })} label="Upload Question Image (Max 8MB)" />
              </div>
            )}
          </div>

          {/* ── SECTION 4: Options ── */}
          <div className="card space-y-4">
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider border-b pb-2">Section 4 — Options (A, B, C, D) *</h2>
            <p className="text-xs text-gray-500">All four option texts are required. Optionally add an image per option. Click the circle to mark the correct answer.</p>
            <div className="space-y-4">
              {['A','B','C','D'].map(opt => (
                <div key={opt} className={`rounded-xl border-2 p-4 transition-all ${form.correctAnswer === opt ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                  <div className="flex items-start gap-3">
                    {/* Correct answer selector */}
                    <button type="button" onClick={() => sf({ correctAnswer: opt })}
                      className={`mt-1 w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-sm border-2 transition-all ${form.correctAnswer === opt ? 'bg-green-500 border-green-500 text-white shadow-md' : 'border-gray-300 text-gray-500 hover:border-green-400 hover:text-green-600'}`}
                      title={`Mark option ${opt} as correct`}>
                      {form.correctAnswer === opt ? <CheckCircle size={16} /> : opt}
                    </button>
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700 w-20">Option {opt} *</span>
                        {form.correctAnswer === opt && (
                          <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded-full">✓ Correct Answer</span>
                        )}
                      </div>
                      <input type="text" className="input-field text-sm" placeholder={`Enter text for option ${opt}...`}
                        value={form[`opt${opt}`]} onChange={e => sf({ [`opt${opt}`]: e.target.value })} />
                      <div>
                        <p className="text-xs text-gray-400 mb-1.5">Option {opt} image (optional)</p>
                        <ImgUploadBtn value={form[`optImg${opt}`]} onChange={v => sf({ [`optImg${opt}`]: v })} label={`Add image for Option ${opt}`} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── SECTION 5: Answer & Explanation ── */}
          <div className="card space-y-4">
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider border-b pb-2">Section 5 — Answer &amp; Explanation</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Correct Answer *</label>
              <div className="flex gap-3">
                {['A','B','C','D'].map(opt => (
                  <button key={opt} type="button" onClick={() => sf({ correctAnswer: opt })}
                    className={`w-12 h-12 rounded-xl font-bold text-sm border-2 transition-all ${form.correctAnswer === opt ? 'bg-green-500 border-green-500 text-white shadow-md scale-110' : 'border-gray-200 text-gray-600 hover:border-green-300 bg-white'}`}>
                    {opt}
                  </button>
                ))}
                <span className="flex items-center text-sm text-gray-500 ml-2">
                  Selected: <strong className="ml-1 text-green-600">Option {form.correctAnswer}</strong>
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-2">You can also click the circle button next to each option in Section 4.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Explanation <span className="text-gray-400 text-xs">(optional — shown after exam submission)</span></label>
              <textarea className="input-field" rows={3} placeholder="Explain why the selected answer is correct..."
                value={form.explanation} onChange={e => sf({ explanation: e.target.value })} />
            </div>
          </div>

          {/* ── Action Buttons ── */}
          <div className="flex items-center gap-3 pb-8 flex-wrap">
            <button type="button" onClick={() => navigate('/admin/questions')} className="btn-secondary flex items-center gap-2">
              <ArrowLeft size={16} /> Cancel
            </button>
            <button type="button" onClick={() => setPreview(true)} className="btn-secondary flex items-center gap-2">
              <Eye size={16} /> Preview
            </button>
            <button type="submit" disabled={saving} onClick={handleSave} className="btn-primary flex items-center gap-2 min-w-36">
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              {saving ? 'Saving...' : (isEdit ? 'Update Question' : 'Save Question')}
            </button>
          </div>

        </form>
      )}
    </div>
  );
}
